import { useState, useEffect, createContext, useRef } from 'react'
import { adminService } from '../services/adminService'
import { ROUTES } from '../config/constants'
import { devWarn, devError } from '../utils/devLog'

export const AdminAuthContext = createContext(null)

// Session timeout: 7 days (matching backend JWT expiry)
const SESSION_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000 // 7 days in milliseconds
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes of inactivity

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null)
  const [loading, setLoading] = useState(true)
  const inactivityTimerRef = useRef(null)
  const sessionStartTimeRef = useRef(null)

  const resetInactivityTimer = () => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
    }

    // Set new inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      devWarn('Session expired due to inactivity')
      logout()
    }, INACTIVITY_TIMEOUT_MS)
  }

  const checkSessionExpiry = () => {
    const token = localStorage.getItem('adminToken')
    if (!token) return false

    try {
      // Decode JWT to check expiry (basic check, backend validates)
      const payload = JSON.parse(atob(token.split('.')[1]))
      const expiryTime = payload.exp * 1000 // Convert to milliseconds
      const now = Date.now()

      if (now >= expiryTime) {
        devWarn('Session expired')
        logout()
        return false
      }

      // Check if session start time + timeout exceeded
      if (sessionStartTimeRef.current) {
        const sessionAge = now - sessionStartTimeRef.current
        if (sessionAge >= SESSION_TIMEOUT_MS) {
          devWarn('Session timeout exceeded')
          logout()
          return false
        }
      }

      return true
    } catch (error) {
      devError('Error checking session expiry', error)
      logout()
      return false
    }
  }

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('adminToken')
    const adminData = localStorage.getItem('adminData')
    
    if (token && adminData) {
      try {
        // Check session expiry before setting admin
        if (checkSessionExpiry()) {
          setAdmin(JSON.parse(adminData))
          sessionStartTimeRef.current = Date.now()
          setLoading(false)

          // Set up inactivity timer
          resetInactivityTimer()

          // Listen for user activity
          const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
          const handleActivity = () => {
            if (checkSessionExpiry()) {
              resetInactivityTimer()
            }
          }

          events.forEach(event => {
            document.addEventListener(event, handleActivity, true)
          })

          // Periodic session check (every 5 minutes)
          const sessionCheckInterval = setInterval(() => {
            if (!checkSessionExpiry()) {
              clearInterval(sessionCheckInterval)
            }
          }, 5 * 60 * 1000)

          // Cleanup
          return () => {
            events.forEach(event => {
              document.removeEventListener(event, handleActivity, true)
            })
            clearInterval(sessionCheckInterval)
            if (inactivityTimerRef.current) {
              clearTimeout(inactivityTimerRef.current)
            }
          }
        }
      } catch (error) {
        devError('Error parsing admin data', error)
        localStorage.removeItem('adminToken')
        localStorage.removeItem('adminData')
      }
    }
    setLoading(false)
  }, [])

  const login = async (username, password) => {
    try {
      const response = await adminService.login(username, password)
      if (response.success) {
        const { token, admin: adminData } = response
        localStorage.setItem('adminToken', token)
        localStorage.setItem('adminData', JSON.stringify(adminData))
        setAdmin(adminData)
        sessionStartTimeRef.current = Date.now()
        
        // Reset inactivity timer on login
        resetInactivityTimer()
        
        return { success: true }
      }
      return { success: false, error: response.error || 'Login failed' }
    } catch (error) {
      return { success: false, error: error.message || 'Login failed' }
    }
  }

  const logout = () => {
    // Clear timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
    
    localStorage.removeItem('adminToken')
    localStorage.removeItem('adminData')
    setAdmin(null)
    sessionStartTimeRef.current = null
    window.location.href = ROUTES.ADMIN_LOGIN
  }

  const isAuthenticated = () => {
    return !!localStorage.getItem('adminToken')
  }

  return (
    <AdminAuthContext.Provider value={{ admin, login, logout, isAuthenticated, loading }}>
      {children}
    </AdminAuthContext.Provider>
  )
}
